export enum DisplayType {
	ITEM              = 1,
	COUPON            = 2,
	VIDEO             = 3,
	LINK              = 5,
	ITEMVIDEO         = 6,
	PAGELINK          = 7,
	CUSTOMHTML        = 8,
	HIDEINFO          = 9,
	SIDESUBITEMS      = 10,
	CUSTOMLIST        = 11,
	SHAREFB           = 12,
	SHARETWITTER      = 13,
	ITEMSIDELIST      = 14,
	IFRAME            = 15,
	ONLYFEATUREHTML   = 16,
	SPOTLIGHT         = 17,
	ITEMSIDEGRID      = 18,
	TOMBSTONE         = 19,
	KEYFRAME          = 20,
	CATEGORYSPOTLIGHT = 21,
	ITEMGROUP         = 22,
	ITEMCORRECTION    = 23,
	TEXTCONTENT       = 24,
	COUPONV2          = 25,
	COUPONV3          = 26
}

export interface StoreInfoJSON {
	info: {
		select_stores: {
			address: string;
			city: string;
			distance: number;
			id: number;
			lat: number;
			lon: number;
			merchant_id: number;
			merchant_store_code: string;
			name: string;
			postal_code: string;
			province: string;
			source: string;
		}[];
	};
}

export interface FlyerItemJSON {
	id: number;
	flyer_id: number;
	short_name: string;
	brand: string;
	name: string;
	price: string;
	display_type: DisplayType;
	discount: number;
	left: number;
	bottom: number;
	right: number;
	top: number;
	valid_from: string;
	valid_to: string;
	available_to: string;
	video_url: string;
	text_areas: {
		id: number,
		left: number,
		bottom: number,
		right: number,
		top: number
	}[];
	cutout_image_url: string;
	page_destination: number;
	print_id: string;
}

// These fields are returned by Backflipp, but are missing...
// language: number
// popularity: number
// premium_thumbnail_url: string
// seed: number
// featured_items: []
// analytics_payload: string
// display_type: string
export interface FlyerMetadataJSON {
	available_from: string;
	available_to: string;
	budget_id: number;
	categories: string[];
	categories_csv: string;
	flyer_run_id: number;
	flyer_type_id: number;
	height: number;
	id: number;
	locale: string;
	merchant: string;
	merchant_id: number;
	merchant_logo: string;
	name: string;
	path: string;
	postal_code: string;
	premium: boolean;
	priority: number;
	relationship: boolean;
	resolutions: number[];
	resolutions_csv: string;
	is_store_select: boolean;
	thumbnail_url: string;
	thumbnail_hashed_key: string;
	updated_at: string;
	valid_from: string;
	valid_to: string;
	web_indexed: boolean;
	width: number;
	tracking_urls: TrackingUrlJSON[];
}

export class TrackingUrlJSON {
	event_type: number;
	url: string;
}

export interface FlyerJSON {
	has_corrections: boolean;
	items: FlyerItemJSON[];
	pages: {
		bottom: number;
		id: number;
		left: number;
		name: string;
		page: number;
		right: number;
		top: number;
	}[];

	flyer?: any;
}

export interface ItemJSON {
	item: {
		id: number;
		flyer_id: number;
		brand: string;
		display_type: DisplayType;
		merchant: string;
		merchant_logo: string;
		name: string;
		image_url: string;
		cutout_image_url: string;
		description: string;
		current_price: string;
		current_price_range: string;
		pre_price_text: string;
		category: string;
		price_text: string;
		sale_story: string;
		original_price: string;
		original_price_range: string;
		in_store_only: boolean;
		disclaimer_text: string;
		flyer_disclaimer_text: string;
		valid_from: string;
		valid_to: string;
		timezone: number;
		sku: string;
		ttm_label: string;
		ttm_url: string;
		show_details: boolean;
		review_average_rating: number;
		review_samples: {
			body: string;
			byline: string;
			date: string;
			rating: number;
			title: string;
		}[];
		specs: {
			name: string;
			value: string;
		}[];
		features: {
			text: string;
		}[];

		// iframe_display_width: number;
		// iframe_display_height: number;
		// show_price: string;
		// url: string;
		// web_indexed: boolean;
		// video_url: string;
	};
}

// NOTE: These fields are currently returned via Backflipp, but are not accounted for.
// because they are still fluctuating... (potentially need to add/utilize later)
// - brand_id: number
// - upc: string
// - manugacturer_id: string?? --> it might be removed from Backflipp API soon.
export interface EcomItemJSON {
	ecom_item: {
		brand: string;
		buy_online: boolean;
		buy_in_store: boolean;
		colour: string;
		country: string;
		current_price: number;
		description: string;
		features: {
			text: string;
		}[];
		google_categories: string[];
		id: number;
		language: string;
		media: EcomItemMediaObj[];
		merchant: string;
		merchant_id: number;
		merchant_logo: string;
		merchant_categories: string[];
		model_number: string;
		name: string;
		nutritional_info: string[];
		original_price: number;
		review_samples: EcomItemReviewsObj[];
		ship_to_home: boolean;
		ship_to_store: boolean;
		size: string;
		sku: string;
		specs: EcomItemSpecObj[];
		unique_id: string;
		url: string;
		web_id: string;
	};
}

export interface DataJSON {
	category_sort_csv: string;
	coupon_category_sort_csv: string;
	coupons: {
		available_from: string;
		available_to: string;
		barcode_image_url: string;
		brand: string;
		brand_logo_url: string;
		categories: string[];
		coupon_id: number;
		coupon_image_url: string;
		coupon_type: string; // Really an enum?
		coupon_vendor_id: number; // Really an enum?
		dollars_off: number;
		merchant_id: number;
		merchant_logo_url: string;
		merchant_name: string;
		percent_off: number;
		postal_code: string;
		priority: number;
		promotion_text: string;
		qualifying_quantity: number;
		redemption_method: string;
		reward_quantity: number;
		sale_story: string;
		timezone: number;
		use_sales_story: boolean;
		valid_from: string;
		valid_to: string;
	}[];
	flyer_item_coupons: {
		bottom: number;
		coupon_dollars_off: number;
		coupon_id: number;
		display_final_price: boolean;
		display_type: number;
		final_price: number;
		flyer_id: number;
		flyer_item_id: number;
		id: number;
		merchant: string;
		merchant_logo: string;
		item_current_price: number;
		item_cutout_url: string;
		item_dollars_off: number;
		item_original_price: number;
		item_rank: number;
		left: number;
		percent_off: number;
		right: number;
		top: number;
		total_savings: number;
	}[];
	flyers: FlyerMetadataJSON[];
	loyalty_program_coupons: {
		id: number;
		loyalty_program_id: number;
		coupon_id: number;
		external_id: number;
		valid_from: Date;
		valid_to: Date;
		available_to: Date;
		available_from: Date;
	}[];
}

export interface LoyaltyProgramJSON {
	loyalty_programs: {
		id: number;
		name: string;
		is_load_to_card: boolean;
		is_rebate: boolean;
		redemption_method: string;
		validation_method: string;
		phone_regex: string;
		pin_regex: string;
		card_regex: string;
		card_image_url: string;
		large_logo_image_url: string;
		medium_logo_image_url: string;
		small_logo_image_url: string;
		logo_backing_colour: string;
	}[];
	loyalty_program_merchants: {
		id: number;
		loyalty_program_id: number;
		merchant_id: number;
	}[];
}

export interface MultiEcomItemJSON {
	ecom_items: EcomItemSummaryJSON[];
}

export interface SearchItemJSON {
	left: number;
	right: number;
	top: number;
	bottom: number;
	flyer_id: number;
	merchant_image_url: string;
	flyer_item_id: number;
	clipping_image_url: string;
	name: string;
}

type EcomItemDisplayType = "show" | "link" | "hide";
export interface EcomItemSummaryJSON {
	id: number;
	name: string;
	current_price: number;
	original_price: number;
	description: string;
	image_url: string;
	merchant_id: number;
	merchant_logo: string;
	merchant: string;
	display: EcomItemDisplayType;
}

export interface SearchJSON {
	merchants: {
		id: number,
		name: string,
		logo_url: string
	}[];
	items: SearchItemJSON[];
	coupons: number[];
	ecom_items: EcomItemSummaryJSON[];
}

export interface TermsJSON {
	refreshed_at: string;
	coupons: {
		id: number;
		disclaimer_text: string;
	}[];
}

export interface EcomItemMediaObj {
	url: string;
	media_type: string;
	media_name: string;
}

export interface EcomItemSpecObj {
	name: string;
	value: string;
}

export interface EcomItemReviewsObj {
	author: string;
	title: string;
	rating: number;
	date: string;
	body: string;
}
